import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Divider,
  dividerClasses,
  Grid,
  IconButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import { ROUTE_STRING } from 'constants/RouteString';
import DockerAppTypes from 'constants/DockerAppTypes';
import { grey } from '@mui/material/colors';
import { DataGrid } from '@mui/x-data-grid';
import useGetDetailAppPackage from 'redux/slices/appPackage/hooks/useGetDetailAppPackage';
import { Code } from 'react-content-loader';
import UpgradeVersionButton from './components/Common/UpgradeVersionButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteAppPackageAction from './components/Common/DeleteAppPackageAction';
import DeleteIcon from '@mui/icons-material/Delete';
import Error404 from 'pages/errorPages/Error404';
import useDeleteAppComponent from 'redux/slices/appPackage/hooks/useDeleteAppComponent';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useModal from '@tenant/utility/hooks/useModal';
import DeleteDialog from '@tenant/core/App/Dialog/DeleteDialog';
import { deepClone } from '@tenant/utility/helper/Utils';
import { toast } from 'react-toastify';
import AddComponentDialog from './components/Common/AddComponentDialog';
import AppsContainer from '@tenant/core/AppsContainer';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';

const Detail = () => {
  const canDeleteComponent = useAllowedAccess([
    AuthorizePermission.APP_PACKAGE_APP_COMPONENT_DELETE,
  ]);
  const { id } = useParams();
  const { data, isLoading, refetch, isRefetching, isError } =
    useGetDetailAppPackage(id);
  const deleteComponentMutate = useDeleteAppComponent();
  const loading = isLoading || isRefetching;
  const item = data?.data ?? {};
  const navigate = useNavigate();
  const [selected, setSelected] = useState();
  const onClickBackButton = () => {
    navigate(ROUTE_STRING.APP_PACKAGE.LIST);
  };
  const {
    visible: visibleDelete,
    onShow: onShowDelete,
    onClose: onCloseDelete,
  } = useModal();

  const handleDelete = () => {
    deleteComponentMutate.mutate(
      { appPackageId: id, componentId: selected?.Id },
      {
        onSuccess() {
          toast.success('Delete App Component successfully');
          onCloseDelete();
          refetch();
        },
      },
    );
  };

  const handleShowDelete = (item) => {
    setSelected(deepClone(item));
    onShowDelete();
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Component Name',
      width: 250,
      sortable: true,
      flex: 1,
    },
    {
      field: 'TargetPort',
      headerName: 'Target Port',
      width: 125,
      sortable: false,
      renderCell: (params) => params.value ?? '-',
    },
    {
      field: 'DeploymentType',
      headerName: 'Deployment Type',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box
          component={'span'}
          sx={{ width: '100%', display: 'block' }}
          className='MuiDataGrid-cellContent'
        >
          {DockerAppTypes.find((e) => e.Value === params.value)?.Description}
        </Box>
      ),
    },
    {
      field: 'Delete',
      headerName: ' ',
      width: 75,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <IconButton
          color='error'
          title='Delete'
          onClick={() => handleShowDelete(params.row)}
        >
          <DeleteOutlineIcon />
        </IconButton>
      ),
    },
  ].filter((e) => (!canDeleteComponent ? e.field !== 'Delete' : true));

  if (isError) {
    return <Error404 />;
  }

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.APP_PACKAGE.LIST}>App Packages</Link>
          </Typography>
          <Typography color='text.primary'>
            {data?.data?.Name ?? '-'}
          </Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 4,
            }}
            component='span'
            mr={{ xs: 2, sm: 4 }}
          >
            <AppTooltip title={<IntlMessages id='common.back' />}>
              <ArrowBackIcon
                sx={{
                  color: 'text.secondary',
                }}
                onClick={onClickBackButton}
              />
            </AppTooltip>
            {loading ? (
              <Skeleton width={50} />
            ) : (
              <Typography variant='h4' sx={{ cursor: 'auto' }}>
                {item.Name}{' '}
                <IconButton title='Refresh' size='small' onClick={refetch}>
                  <RefreshIcon fontSize='inherit' />
                </IconButton>
              </Typography>
            )}
          </Box>
          {isLoading || isRefetching ? (
            <Skeleton sx={{ width: 100 }} />
          ) : (
            <Box
              display={'flex'}
              alignItems={'center'}
              gap={2}
              sx={{
                [`& .${dividerClasses.root}`]: {
                  height: 20,
                },
              }}
            >
              <AllowedAccess
                permissionIds={[AuthorizePermission.APP_PACKAGE_UPDATE]}
              >
                {!data?.data?.IsLatest && (
                  <UpgradeVersionButton
                    appPackageId={id}
                    callbackSuccess={refetch}
                  />
                )}
              </AllowedAccess>

              <AllowedAccess
                permissionIds={[AuthorizePermission.APP_PACKAGE_DELETE]}
              >
                <DeleteAppPackageAction
                  selected={item}
                  trigger={
                    <IconButton variant='outlined' color='error' title='Delete'>
                      <DeleteIcon />
                    </IconButton>
                  }
                  handleSubmitDelete={onClickBackButton}
                />
              </AllowedAccess>
            </Box>
          )}
        </Box>
      </AppsHeader>

      <Box p={4} mb={5}>
        {!(isLoading || isRefetching) && (
          <Box>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={12}>
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: 'none',
                    backgroundColor: grey[50],
                    pl: 5,
                    pr: 5,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='h4'> Information </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={4}>
                        <ListItemText
                          primary='Name'
                          secondary={item.Name ?? '-'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ListItemText
                          primary='MarketPlace App'
                          secondary={
                            item.App?.Name ? (
                              <Link
                                to={ROUTE_STRING.LIST_APP.DETAIL.replace(
                                  ':appId',
                                  item.App?.Id,
                                )}
                              >
                                {item.App?.Name}
                              </Link>
                            ) : (
                              '-'
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ListItemText
                          primary='Version'
                          secondary={item.Version ?? ''}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <ListItemText
                          primary='Description'
                          secondary={item.Description ?? '-'}
                          secondaryTypographyProps={{
                            sx: {
                              whiteSpace: 'pre',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Box
                  mb={5}
                  mt={5}
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography variant='h4'> App Components </Typography>
                  <AllowedAccess
                    permissionIds={[
                      AuthorizePermission.APP_PACKAGE_APP_COMPONENT_CREATE,
                    ]}
                  >
                    <AddComponentDialog
                      appPackageId={id}
                      callbackAfterSubmit={refetch}
                    />
                  </AllowedAccess>
                </Box>

                <Box style={{ height: 'auto', width: '100%' }}>
                  <DeleteDialog
                    visible={visibleDelete}
                    loading={deleteComponentMutate.isPending}
                    name={selected?.Name}
                    handleClose={onCloseDelete}
                    handleSave={handleDelete}
                  />

                  <DataGrid
                    hideFooter
                    autoHeight
                    disableColumnSelector
                    disableColumnMenu
                    rows={item?.AppComponents ?? []}
                    columns={columns}
                    getRowId={(row) => row.Id}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '& [data-field="RequiredEnviromentVariables"] .MuiDataGrid-cellContent':
                        {
                          whiteSpace: 'pre',
                          pt: 3,
                          pb: 3,
                        },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {(isLoading || isRefetching) && <Code />}
      </Box>
    </AppsContainer>
  );
};

export default React.memo(Detail);
